import { createFileRoute, Link } from "@tanstack/react-router";
import { AdminGate } from "@/components/AdminGate";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { getGscSnapshot } from "@/lib/gsc.functions";
import type { GscRow, GscSnapshot } from "@/lib/gsc.server";

export const Route = createFileRoute("/admin/search-console")({
  head: () => ({
    meta: [
      { title: "Google Search Console Dashboard | AutoSpecData" },
      {
        name: "description",
        content:
          "Live Google Search Console dashboard: clicks, impressions, CTR, average position, top queries, top pages and sitemap status.",
      },
      { property: "og:title", content: "Google Search Console Dashboard | AutoSpecData" },
      {
        property: "og:description",
        content:
          "Live Google Search Console dashboard: clicks, impressions, CTR, average position, top queries, top pages and sitemap status.",
      },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  ssr: false,
  component: GscPage,
});

function GscPage() {
  return (
    <AdminGate>
      <GscDashboard />
    </AdminGate>
  );
}

const num = (n: number | undefined) => (n == null ? "—" : Math.round(n).toLocaleString());
const pct = (n: number | undefined) => (n == null ? "—" : `${(n * 100).toFixed(2)}%`);
const pos = (n: number | undefined) => (n == null ? "—" : n.toFixed(1));

function Delta({ now, prev, invert }: { now?: number | undefined; prev?: number | undefined; invert?: boolean | undefined }) {
  if (now == null || prev == null || prev === 0) return null;
  const diff = ((now - prev) / prev) * 100;
  const good = invert ? diff < 0 : diff > 0;
  return (
    <span className={`ml-2 text-xs font-medium ${good ? "text-primary" : "text-destructive"}`}>
      {diff > 0 ? "+" : ""}
      {diff.toFixed(1)}%
    </span>
  );
}

function Sparkline({ rows }: { rows: GscRow[] }) {
  if (rows.length < 2) return null;
  const vals = rows.map((r) => r.impressions ?? 0);
  const max = Math.max(...vals, 1);
  const w = 600;
  const h = 90;
  const pts = vals
    .map((v, i) => `${(i / (vals.length - 1)) * w},${h - (v / max) * (h - 6) - 3}`)
    .join(" ");
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="mt-4 h-24 w-full" preserveAspectRatio="none" role="img" aria-label="Impressions trend">
      <polyline points={pts} fill="none" stroke="currentColor" strokeWidth="2" className="text-primary" />
    </svg>
  );
}

function Table({ title, rows, label }: { title: string; rows: GscRow[]; label: string }) {
  return (
    <section className="mt-10">
      <h2 className="text-xl font-semibold text-foreground">{title}</h2>
      {rows.length === 0 ? (
        <p className="mt-3 text-sm text-muted-foreground">No reported data for this period.</p>
      ) : (
        <div className="mt-3 overflow-x-auto rounded-lg border border-border">
          <table className="w-full text-sm">
            <thead className="bg-secondary/50 text-left text-muted-foreground">
              <tr>
                <th className="px-3 py-2 font-medium">{label}</th>
                <th className="px-3 py-2 font-medium">Clicks</th>
                <th className="px-3 py-2 font-medium">Impressions</th>
                <th className="px-3 py-2 font-medium">CTR</th>
                <th className="px-3 py-2 font-medium">Position</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.keys.join("|")} className="border-t border-border">
                  <td className="max-w-[26rem] truncate px-3 py-2 text-foreground">{r.keys[0]}</td>
                  <td className="px-3 py-2">{num(r.clicks)}</td>
                  <td className="px-3 py-2">{num(r.impressions)}</td>
                  <td className="px-3 py-2">{pct(r.ctr)}</td>
                  <td className="px-3 py-2">{pos(r.position)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

const VERDICT: Record<string, { label: string; tone: string; note: string }> = {
  "incomplete-reporting": {
    label: "Today still processing",
    tone: "border-border bg-card text-muted-foreground",
    note: "Hourly totals for today exceed the finalised daily figure — the daily number will revise upward. Do not treat today as a drop.",
  },
  "real-drop": {
    label: "Genuine drop",
    tone: "border-destructive/40 bg-destructive/10 text-destructive",
    note: "Last 24h impressions are down 40%+ on the prior 24h and reporting is complete. This is a real ranking or coverage change.",
  },
  stable: {
    label: "Stable",
    tone: "border-border bg-card text-muted-foreground",
    note: "Last 24h is within normal range of the prior 24h.",
  },
  unknown: {
    label: "Not enough hourly data",
    tone: "border-border bg-card text-muted-foreground",
    note: "Fewer than 24 hours of hourly rows returned for this property.",
  },
};

function Reconciliation({ data }: { data: GscSnapshot }) {
  const r = data.recon;
  if (!r) return null;
  const v = VERDICT[r.verdict] ?? VERDICT["unknown"]!;
  const rows: { label: string; a?: number; b?: number; fmt: (n?: number) => string; invert?: boolean }[] = [
    { label: "Impressions", a: r.last24.impressions, b: r.prior24.impressions, fmt: num },
    { label: "Clicks", a: r.last24.clicks, b: r.prior24.clicks, fmt: num },
    { label: "CTR", a: r.last24.ctr, b: r.prior24.ctr, fmt: pct },
    { label: "Avg position", a: r.last24.position, b: r.prior24.position, fmt: pos, invert: true },
  ];
  return (
    <section className="mt-10">
      <h2 className="text-xl font-semibold text-foreground">Hourly vs daily reconciliation</h2>
      <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
        Compares the last complete 24 hours against the 24 hours before it, then checks the hourly
        sum for today against Google&apos;s finalised daily number. That gap is how you tell reporting
        lag apart from an actual loss of impressions.
      </p>

      <div className={`mt-4 rounded-lg border p-4 text-sm ${v.tone}`}>
        <span className="font-semibold">{v.label}.</span> {v.note}
      </div>

      <div className="mt-4 overflow-x-auto rounded-lg border border-border">
        <table className="w-full text-sm">
          <thead className="bg-secondary/50 text-left text-muted-foreground">
            <tr>
              <th className="px-3 py-2 font-medium">Metric</th>
              <th className="px-3 py-2 font-medium">Last 24h</th>
              <th className="px-3 py-2 font-medium">Prior 24h</th>
              <th className="px-3 py-2 font-medium">Change</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.label} className="border-t border-border">
                <td className="px-3 py-2 text-foreground">{row.label}</td>
                <td className="px-3 py-2">{row.fmt(row.a)}</td>
                <td className="px-3 py-2">{row.fmt(row.b)}</td>
                <td className="px-3 py-2">
                  <Delta now={row.a} prev={row.b} invert={row.invert} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-4 grid gap-4 sm:grid-cols-3">
        <div className="rounded-lg border border-border bg-card p-4">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">Today, hourly sum</p>
          <p className="mt-1 text-2xl font-semibold text-foreground">{num(r.hourlySumToday.impressions)}</p>
          <p className="text-xs text-muted-foreground">{num(r.hourlySumToday.clicks)} clicks</p>
        </div>
        <div className="rounded-lg border border-border bg-card p-4">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">Today, daily report</p>
          <p className="mt-1 text-2xl font-semibold text-foreground">
            {r.dailyToday ? num(r.dailyToday.impressions) : "—"}
          </p>
          <p className="text-xs text-muted-foreground">
            {r.dailyToday ? `${num(r.dailyToday.clicks)} clicks` : "not published yet"}
          </p>
        </div>
        <div className="rounded-lg border border-border bg-card p-4">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">Daily ÷ hourly</p>
          <p className="mt-1 text-2xl font-semibold text-foreground">
            {r.lagRatio == null ? "—" : `${(r.lagRatio * 100).toFixed(0)}%`}
          </p>
          <p className="text-xs text-muted-foreground">under 80% = still processing</p>
        </div>
      </div>

      {r.dailyTail.length > 0 && (
        <div className="mt-4 overflow-x-auto rounded-lg border border-border">
          <table className="w-full text-sm">
            <thead className="bg-secondary/50 text-left text-muted-foreground">
              <tr>
                <th className="px-3 py-2 font-medium">Day</th>
                <th className="px-3 py-2 font-medium">Clicks</th>
                <th className="px-3 py-2 font-medium">Impressions</th>
                <th className="px-3 py-2 font-medium">CTR</th>
                <th className="px-3 py-2 font-medium">Position</th>
              </tr>
            </thead>
            <tbody>
              {r.dailyTail.map((d) => (
                <tr key={d.keys[0]} className="border-t border-border">
                  <td className="px-3 py-2 text-foreground">{d.keys[0]}</td>
                  <td className="px-3 py-2">{num(d.clicks)}</td>
                  <td className="px-3 py-2">{num(d.impressions)}</td>
                  <td className="px-3 py-2">{pct(d.ctr)}</td>
                  <td className="px-3 py-2">{pos(d.position)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function GscDashboard() {

  const [days, setDays] = useState(28);
  const [siteUrl, setSiteUrl] = useState<string | undefined>(undefined);
  const fetchSnapshot = useServerFn(getGscSnapshot);

  const { data, isFetching, refetch, error } = useQuery<GscSnapshot>({
    queryKey: ["gsc", days, siteUrl],
    queryFn: () => fetchSnapshot({ data: { days, siteUrl } }) as Promise<GscSnapshot>,
    staleTime: 0,
    refetchOnWindowFocus: false,
  });

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-8">
      <nav className="text-sm text-muted-foreground">
        <Link to="/" className="hover:text-foreground">
          Home
        </Link>
        <span className="mx-2">/</span>
        <Link to="/admin/seo" className="hover:text-foreground">
          SEO
        </Link>
        <span className="mx-2">/</span>
        <span className="text-foreground">Search Console</span>
      </nav>

      <div className="mt-4 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Google Search Console</h1>
          <p className="mt-2 max-w-2xl text-muted-foreground">
            Live data pulled straight from Search Console every time you load or refresh this page —
            no cached snapshot in between.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="rounded-md border border-border bg-background px-3 py-2 text-sm"
            aria-label="Date range"
          >
            <option value={7}>Last 7 days</option>
            <option value={28}>Last 28 days</option>
            <option value={90}>Last 3 months</option>
            <option value={180}>Last 6 months</option>
            <option value={480}>Last 16 months</option>
          </select>
          <button
            onClick={() => refetch()}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-60"
            disabled={isFetching}
          >
            {isFetching ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </div>

      {error && (
        <p className="mt-6 rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
          {(error as Error).message}
        </p>
      )}

      {!data && isFetching && (
        <p className="mt-8 text-sm text-muted-foreground">Loading live Search Console data…</p>
      )}

      {data?.status === "selection_required" && (
        <div className="mt-6 rounded-lg border border-border bg-card p-4">
          <p className="text-sm text-foreground">
            Several verified properties cover this site. Pick the one to report on:
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            {data.candidates?.map((c) => (
              <button
                key={c}
                onClick={() => setSiteUrl(c)}
                className="rounded-md border border-border px-3 py-2 text-sm hover:bg-secondary"
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      )}

      {data?.status === "unavailable" && (
        <p className="mt-6 rounded-lg border border-border bg-card p-4 text-sm text-muted-foreground">
          Search Console data is unavailable right now: {data.message}
        </p>
      )}

      {data?.status === "ok" && (
        <>
          <p className="mt-6 text-xs text-muted-foreground">
            Property {data.siteUrl} · {data.range?.start} → {data.range?.end} · fetched{" "}
            {new Date(data.refreshedAt).toLocaleString()}
          </p>

          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              { label: "Clicks", value: num(data.totals?.clicks), now: data.totals?.clicks, prev: data.prevTotals?.clicks },
              { label: "Impressions", value: num(data.totals?.impressions), now: data.totals?.impressions, prev: data.prevTotals?.impressions },
              { label: "Average CTR", value: pct(data.totals?.ctr), now: data.totals?.ctr, prev: data.prevTotals?.ctr },
              { label: "Average position", value: pos(data.totals?.position), now: data.totals?.position, prev: data.prevTotals?.position, invert: true },
            ].map((c) => (
              <div key={c.label} className="rounded-lg border border-border bg-card p-4">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">{c.label}</p>
                <p className="mt-1 text-2xl font-semibold text-foreground">
                  {c.value}
                  <Delta now={c.now} prev={c.prev} invert={c.invert} />
                </p>
              </div>
            ))}
          </div>

          <div className="mt-6 rounded-lg border border-border bg-card p-4">
            <p className="text-sm font-medium text-foreground">Impressions by day</p>
            <Sparkline rows={data.byDate ?? []} />
          </div>

          <Reconciliation data={data} />

          <Table title="Top queries" rows={data.queries ?? []} label="Query" />

          <Table title="Top pages" rows={data.pages ?? []} label="Page" />
          <div className="grid gap-6 md:grid-cols-2">
            <Table title="Devices" rows={data.devices ?? []} label="Device" />
            <Table title="Countries" rows={data.countries ?? []} label="Country" />
          </div>

          <section className="mt-10">
            <h2 className="text-xl font-semibold text-foreground">Sitemaps</h2>
            {(data.sitemaps ?? []).length === 0 ? (
              <p className="mt-3 text-sm text-muted-foreground">No sitemaps reported.</p>
            ) : (
              <div className="mt-3 overflow-x-auto rounded-lg border border-border">
                <table className="w-full text-sm">
                  <thead className="bg-secondary/50 text-left text-muted-foreground">
                    <tr>
                      <th className="px-3 py-2 font-medium">Sitemap</th>
                      <th className="px-3 py-2 font-medium">Submitted URLs</th>
                      <th className="px-3 py-2 font-medium">Indexed</th>
                      <th className="px-3 py-2 font-medium">Errors</th>
                      <th className="px-3 py-2 font-medium">Warnings</th>
                      <th className="px-3 py-2 font-medium">Last read</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.sitemaps?.map((s) => (
                      <tr key={s.path} className="border-t border-border">
                        <td className="max-w-[22rem] truncate px-3 py-2 text-foreground">{s.path}</td>
                        <td className="px-3 py-2">{num(s.submitted)}</td>
                        <td className="px-3 py-2">{num(s.indexed)}</td>
                        <td className="px-3 py-2">{s.errors ?? 0}</td>
                        <td className="px-3 py-2">{s.warnings ?? 0}</td>
                        <td className="px-3 py-2 text-muted-foreground">
                          {s.lastDownloaded ? new Date(s.lastDownloaded).toLocaleDateString() : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <p className="mt-8 text-xs text-muted-foreground">
            Google itself finalizes Search Console data with a 1–3 day delay, so the most recent days
            can still rise. Everything shown here is the freshest data Google will serve.
          </p>
        </>
      )}
    </div>
  );
}
